/**
 * CourseTipsCard — the AI-drafted admission tips that headline
 * Cluster 2 · "Applying & getting in" on the course page. Renders
 * 5-8 concrete, non-generic tips written by Claude Sonnet 4.6 and
 * cached in `degrees.course_tips_ai` (see
 * `backend/scripts/generate_course_tips.py`).
 *
 * We only ship tips that were either editorially reviewed OR that the
 * model self-rated ≥8/10 for specificity — filtered server-side, so
 * this component just renders what the endpoint gives it.
 *
 * The tip card is intentionally editorial, not a "callout box":
 *  - Warm background band so it reads as a curated recommendation
 *  - Category chip (application/interview/preparation/…) so families
 *    can skim
 *  - Small "How we drafted this" disclosure at the bottom
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Highlighter from "@/components/app/Highlighter";
import { GraduationCap, Compass, PenTool, CalendarClock, Camera, MessageSquare, Sparkles } from "lucide-react";

const CAT_META = {
  application:  { label: "Application",   icon: PenTool },
  interview:    { label: "Interview",     icon: MessageSquare },
  preparation:  { label: "Preparation",   icon: GraduationCap },
  timing:       { label: "Timing",        icon: CalendarClock },
  essay:        { label: "Personal statement", icon: PenTool },
  portfolio:    { label: "Portfolio",     icon: Camera },
};

export default function CourseTipsCard({ degreeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!degreeId) return;
    let alive = true;
    setLoading(true);
    api
      .get(`/degrees/${degreeId}/course-tips`)
      .then(res => {
        if (!alive) return;
        setData(res.data);
      })
      .catch(err => {
        if (!alive) return;
        setError(err?.message || "load_failed");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [degreeId]);

  if (loading) return null; // silent skeleton — the card is optional
  if (error) return null;
  const tips = data?.tips || [];
  if (tips.length === 0) return null;

  return (
    <div
      data-testid="course-tips-card"
      className="mt-6 rounded-2xl bg-gradient-to-br from-amber-50 via-stone-50 to-white border border-amber-200/60 p-6 sm:p-8 shadow-sm"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-900 text-amber-50">
          <Compass className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[11px] font-underground-sc tracking-[0.18em] text-amber-900/70">
            Advisor briefing
          </div>
          <h4 className="font-editorial text-xl sm:text-2xl text-stone-900 leading-tight">
            Tips for THIS course, by our editorial team
          </h4>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {tips.map((t, idx) => {
          const meta = CAT_META[t.category] || CAT_META.preparation;
          const Icon = meta.icon;
          return (
            <div
              key={t.id || idx}
              data-testid={`course-tip-${idx}`}
              className="rounded-xl bg-white/80 border border-stone-200 p-4 hover:border-amber-300 hover:shadow-md transition"
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-3.5 h-3.5 text-amber-900" aria-hidden />
                <span className="text-[10px] font-underground-sc tracking-wider text-amber-900/80">
                  {meta.label}
                </span>
              </div>
              <div className="font-medium text-stone-900 leading-snug mb-1.5">
                <Highlighter>{t.title}</Highlighter>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed">
                <Highlighter>{t.body}</Highlighter>
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-amber-200/70 flex items-start gap-2 text-[11px] text-stone-500">
        <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" aria-hidden />
        <p>
          Analysis comes from the course's real entry requirements,
          NSS data, curriculum notes and the university's public
          course pages. Every tip is highly specific to this course.
          Editor's review in progress.
        </p>
      </div>
    </div>
  );
}
